// @ts-nocheck
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEvents, EventData } from "@/hooks/useEvents";
import { useCamps as useCollegeCamps, Camp } from "@/hooks/useCamps";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";
import { useNcaaCalendarEvents } from "@/hooks/useNcaaCalendarEvents";
import SportsHubMessages from "./SportsHubMessages";
import { CalendarComponent } from "@/components/calendar/CalendarComponent";
import { supabase } from "@/integrations/supabase/client";
import {
  Search,
  Filter,
  MapPin,
  Calendar,
  Trophy,
  Star,
  ArrowRight,
  CheckCircle,
  Users,
  Target,
  Award,
  TrendingUp,
  Eye,
  Clock,
  Globe,
  Zap,
  Shield,
  Heart,
  Video,
  UserPlus,
  FileText,
  Camera,
  Mic,
  ExternalLink,
  GraduationCap,
  Ruler,
  Mail,
  Phone,
  Rss,
  Bookmark,
  Info
} from "lucide-react";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';

// Props interface for the template
interface SportTemplateProps {
  sportName: string;
  heroImage: string;
  sportDescription: string;
}

// No longer needed - remove CalendarFeed interface

interface RSSFeed {
  name: string;
  url: string;
  category: string;
  enabled: boolean;
  division?: string;
}

interface RSSArticle {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  source: string;
  division?: string;
}

export function SportTemplate({ 
  sportName = "Basketball", 
  heroImage = "https://images.pexels.com/photos/5384617/pexels-photo-5384617.jpeg",
  sportDescription = "Elite basketball recruiting and development opportunities"
}: SportTemplateProps) {
  const navigate = useNavigate();
  const { sportSlug } = useParams<{ sportSlug: string }>();
  const [selectedEventType, setSelectedEventType] = useState("All Types");
  const [rssFeeds, setRssFeeds] = useState<RSSFeed[]>([]);
  // Remove calendar feeds state - no longer needed
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [rssArticles, setRssArticles] = useState<RSSArticle[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<RSSArticle[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<string | null>(null);
  const [calendarView, setCalendarView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [currentSportId, setCurrentSportId] = useState<string | undefined>(undefined);

  // Use calendar events hook
  const { 
    formattedEvents: calendarEvents,
    isLoading: calendarLoading,
    error: calendarError
  } = useCalendarEvents();

  // Use NCAA calendar events hook with sport slug format
  const {
    events: ncaaCalendarEvents,
    loading: ncaaCalendarLoading,
    error: ncaaCalendarError
  } = useNcaaCalendarEvents(sportSlug);

  // Use the events hook to get real events data
  const { 
    events: realEvents, 
    loading: eventsLoading, 
    error: eventsError 
  } = useEvents({
    sport: sportName.includes("'s ") ? sportName.split("'s ")[1] : sportName,
    gender: sportName.includes("'s ") ? sportName.split("'s ")[0] : 'all',
    limit: 6
  });

  // Use the college camps hook to get real camps data
  const { camps: allCollegeCamps, loading: campsLoading, error: campsError } = useCollegeCamps('all');
  
  // Filter camps by sport and gender
  const realCamps = allCollegeCamps.filter(camp => {
    console.log('🏕️ Camp filtering debug:', { 
      campTitle: camp.title, 
      campSport: camp.sport, 
      campGender: camp.gender, 
      sportName,
      parsedSportName: sportName.includes("'s ") ? sportName.split("'s ")[1] : sportName
    });
    
    // Parse sport name from route or display name
    let targetSport = sportName;
    let targetGender = null;
    
    // Handle "Men's Basketball" or "Women's Basketball" format
    if (sportName.includes("'s ")) {
      const parts = sportName.split("'s ");
      targetGender = parts[0]; // "Men" or "Women"
      targetSport = parts[1];  // "Basketball"
    }
    
    // Check if camp sport matches target sport
    const campSport = camp.sport || '';
    const sportMatches = campSport.toLowerCase().includes(targetSport.toLowerCase()) ||
                        (targetSport.toLowerCase() === 'basketball' && campSport.toLowerCase().includes('basketball')) ||
                        (targetSport.toLowerCase() === 'football' && campSport.toLowerCase().includes('football')) ||
                        (targetSport.toLowerCase() === 'baseball' && campSport.toLowerCase().includes('baseball')) ||
                        (targetSport.toLowerCase() === 'soccer' && campSport.toLowerCase().includes('soccer'));
    
    if (!sportMatches) {
      console.log('🏕️ Sport mismatch:', { campSport, targetSport, sportMatches });
      return false;
    }
    
    // Check gender if specified
    if (targetGender) {
      const campGender = camp.gender?.toLowerCase() || '';
      const genderMatches = campSport.toLowerCase().includes(targetGender.toLowerCase()) ||
                           campGender.includes(targetGender.toLowerCase()) ||
                           (targetGender.toLowerCase() === 'men' && (campGender.includes('boy') || campGender.includes('male'))) ||
                           (targetGender.toLowerCase() === 'women' && (campGender.includes('girl') || campGender.includes('female')));
      
      if (!genderMatches) {
        console.log('🏕️ Gender mismatch:', { campSport, campGender, targetGender, genderMatches });
        return false;
      }
    }
    
    console.log('🏕️ Camp matched:', { campTitle: camp.title, campSport, targetSport, targetGender });
    return true;
  });

  // Fetch athletes for this sport on component mount
  useEffect(() => {
    // Clear previous feeds immediately when sport changes
    setRssFeeds([]);
    // Remove calendar feeds cleanup - no longer needed
    setRssArticles([]);
    setFilteredArticles([]);
    setFeedsLoading(true);
    
    // Extract the base sport name without gender prefix
    let baseSportName = sportName;
    let genderFilter = 'all';
    
    // Handle gendered sport names (e.g., "Men's Basketball" -> "Basketball" + gender filter)
    if (sportName.includes("'s ")) {
      const parts = sportName.split("'s ");
      if (parts.length === 2) {
        const gender = parts[0]; // "Men" or "Women"
        baseSportName = parts[1]; // "Basketball"
        genderFilter = gender; // Use "Men" or "Women" directly to match database values
      }
    }
    // Handle URL-style sport names (e.g., "Men Basketball" from "men-basketball" route)
    else if (sportName.toLowerCase().includes('men ') || sportName.toLowerCase().includes('women ')) {
      const parts = sportName.split(' ');
      if (parts.length >= 2) {
        const gender = parts[0]; // "Men" or "Women" 
        baseSportName = parts.slice(1).join(' '); // "Basketball" 
        genderFilter = gender;
      }
    }
    
    console.log('🏀 Sport filtering debug:', {
      originalSportName: sportName,
      baseSportName,
      genderFilter,
      route: window.location.pathname
    });
    
    // Fetch the RSS feeds and calendar feeds for this sport
    fetchSportsHubFeeds(baseSportName, genderFilter);
    
    // Fetch sport ID for messages
    fetchSportId(baseSportName, genderFilter);
    
    // Cleanup function to clear feeds when component unmounts or sport changes
    return () => {
      setRssFeeds([]);
      // Remove calendar feeds cleanup - no longer needed
      setRssArticles([]);
      setFilteredArticles([]);
    };
  }, [sportName]); // Remove fetchAthletes from dependencies to ensure proper re-fetch on sport change

  // Function to get sport ID from current sport name for messages
  const fetchSportId = async (baseSportName: string, genderFilter: string) => {
    try {
      let query = supabase
        .from('sports')
        .select('id')
        .eq('name', baseSportName)
        .eq('is_active', true);

      if (genderFilter !== 'all') {
        query = query.eq('gender', genderFilter);
      }

      const { data } = await query.maybeSingle();
      setCurrentSportId(data?.id);
    } catch (error) {
      console.error('Error fetching sport ID:', error);
    }
  };

  // Fetch RSS feeds from database (calendar feeds no longer needed)
  const fetchSportsHubFeeds = async (sportName: string, gender: string) => {
    try {
      setFeedsLoading(true);
      
      console.log('🔍 Fetching feeds for sport-specific data:', { sportName, gender });
      
      // Query the sports_hub_content table to get RSS feeds for this sport with cache busting
      const cacheKey = `${Date.now()}-${Math.random()}`;
      let query = supabase
        .from('sports_hub_content')
        .select(`
          rss_feeds,
          sports!inner(name, gender)
        `)
        .eq('is_active', true)
        .eq('sports.name', sportName);

      if (gender !== 'all') {
        query = query.eq('sports.gender', gender);
      }

      console.log('🔍 Exact query parameters for feeds:', { 
        sportName, 
        gender, 
        isAll: gender === 'all',
        currentRoute: window.location.pathname,
        timestamp: new Date().toISOString(),
        cacheKey
      });

      const { data, error } = await query.single();

      if (error) {
        console.error('Error fetching sports hub feeds:', error);
        // Clear feeds on error to prevent showing stale data from other sports
        setRssFeeds([]);
        return;
      }

      console.log('🔍 Sports hub feeds data for sport:', { sportName, gender, data });

      if (data) {
        // Filter for enabled RSS feeds only
        const allRssFeeds = data.rss_feeds || [];
        
        console.log('🔍 Raw RSS feeds before filtering:', {
          allRssFeeds: allRssFeeds.map((f: RSSFeed) => ({ name: f.name, enabled: f.enabled }))
        });
        
        const enabledRssFeeds = allRssFeeds.filter((feed: RSSFeed) => feed.enabled === true);
        
        console.log('🔍 Filtered enabled RSS feeds:', { 
          rssFeeds: enabledRssFeeds.length,
          enabledRssFeeds: enabledRssFeeds.map((f: RSSFeed) => ({ name: f.name, enabled: f.enabled }))
        });
        
        setRssFeeds(enabledRssFeeds);
        
        // Map feeds to include division from name if it matches D1/D2/D3 pattern
        const feedsWithDivision = enabledRssFeeds.map((feed: RSSFeed) => ({
          ...feed,
          division: feed.name.match(/D[123]/) ? feed.name : feed.category
        }));
        
        // Fetch RSS articles from the configured feeds
        if (feedsWithDivision.length > 0) {
          fetchRssArticles(feedsWithDivision);
        } else {
          // Use sample data if no RSS feeds configured
          setRssArticles([
            {
              title: `Top 10 ${sportName} Recruits Making Headlines This Week`,
              link: "#",
              pubDate: "2024-03-18",
              description: "Breaking down the latest commitments and recruiting news from around the nation...",
              source: "NCAA News"
            },
            {
              title: `NCAA Tournament Impact on ${sportName} Recruiting`,
              link: "#",
              pubDate: "2024-03-17", 
              description: "How March Madness affects recruiting decisions and coach movements...",
              source: "NCAA News"
            },
            {
              title: `Transfer Portal Update: Key ${sportName} Players on the Move`,
              link: "#",
              pubDate: "2024-03-16",
              description: "Latest updates from the transfer portal and impact on recruiting...",
              source: "NCAA News"
            },
            {
              title: `High School ${sportName} All-American Selections Announced`,
              link: "#",
              pubDate: "2024-03-15",
              description: "The top high school players earn prestigious All-American honors...",
              source: "NCAA News"
            },
            {
              title: `${sportName} Recruiting Rule Changes: What You Need to Know`,
              link: "#",
              pubDate: "2024-03-14",
              description: "Important updates to NCAA recruiting regulations for student-athletes...",
              source: "NCAA News"
            }
          ]);
        }
      } else {
        // No sport-specific data found, set empty arrays instead of fallback data
        console.log('🔍 No sport-specific hub data found for:', { sportName, gender });
        setRssFeeds([]);
      }
    } catch (error) {
      console.error('Error fetching feeds:', error);
      // Set empty arrays on error instead of fallback data
      setRssFeeds([]);
    } finally {
      setFeedsLoading(false);
    }
  };

  // Fetch RSS articles from configured feeds
  const fetchRssArticles = async (feeds: RSSFeed[]) => {
    try {
      console.log('📡 Fetching RSS articles from edge function...', feeds);
      
      if (!feeds || feeds.length === 0) {
        console.warn('⚠️ No RSS feeds provided to fetchRssArticles');
        setRssArticles([]);
        setFilteredArticles([]);
        return;
      }
      
      // Call the edge function to fetch and parse RSS feeds
      const { data, error } = await invokeEdgeFunction('fetch-rss-feeds', {
        body: { feeds }
      });

      console.log('📡 Edge function response:', { data, error });

      if (error) {
        console.error('❌ Error calling fetch-rss-feeds function:', error);
        throw error;
      }

      if (data?.articles && Array.isArray(data.articles)) {
        console.log(`✅ Received ${data.articles.length} articles from RSS feeds`);
        setRssArticles(data.articles);
        setFilteredArticles(data.articles);
      } else {
        console.warn('⚠️ No articles returned from RSS feeds, data:', data);
        setRssArticles([]);
        setFilteredArticles([]);
      }
    } catch (error) {
      console.error('❌ Error fetching RSS articles:', error);
      setRssArticles([]);
      setFilteredArticles([]);
    }
  };

  // Helper function to format recruiting status
  // Transform real events to match UI structure or use fallback events
  const events = realEvents.length > 0 ? realEvents.map(event => ({
    id: event.id,
    title: event.name,
    date: event.start_date ? parseLocalDate(event.start_date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).replace(/(\d+)/, (match) => match.toUpperCase()) : 'TBD',
    location: event.venue_city && event.venue_state 
      ? `${event.venue_city}, ${event.venue_state}` 
      : event.venue_name || 'Location TBD',
    sport: sportName,
    type: event.event_type_name || 'Event',
    description: event.description || `Join us for this exciting ${sportName.toLowerCase()} event.`,
    featured: false,
    cardStyle: "light" as const,
    buttonStyle: "outline" as const,
    price: event.pricing_notes || 'Contact for pricing',
    coachCount: "College coaches attending",
    registrationUrl: event.external_registration_url || null
  })) : [
    // Fallback sample events when no real events are available
    {
      id: 'sample-1',
      title: `Elite ${sportName} Showcase`,
      date: "MARCH 25, 2024",
      location: "Chicago, IL",
      sport: sportName,
      type: "Showcase",
      description: "Top college coaches from Division I programs will be in attendance. Open to juniors and seniors.",
      featured: true,
      cardStyle: "dark" as const,
      buttonStyle: "secondary" as const,
      price: "$150",
      coachCount: "50+ coaches",
      registrationUrl: null
    },
    {
      id: 'sample-2',
      title: `${sportName} Skills Development Camp`,
      date: "APRIL 15, 2024",
      location: "Atlanta, GA",
      sport: sportName,
      type: "Camp",
      description: "3-day intensive camp focusing on fundamental skills and college-level training techniques.",
      featured: false,
      cardStyle: "light" as const,
      buttonStyle: "outline" as const,
      price: "$275",
      coachCount: "25+ coaches",
      registrationUrl: null
    },
    {
      id: 'sample-3',
      title: `${sportName} Combine`,
      date: "MAY 10, 2024",
      location: "Dallas, TX",
      sport: sportName,
      type: "Combine",
      description: "Performance testing and skills evaluation with direct feedback from college recruiters.",
      featured: false,
      cardStyle: "gradient" as const,
      buttonStyle: "dark" as const,
      price: "$125",
      coachCount: "30+ coaches",
      registrationUrl: null
    }
  ];

  // Sample camps for this sport
  const camps = [
    {
      id: 1,
      title: `Larry Hughes Elite ${sportName} Camp`,
      date: "JUNE 15-18, 2024",
      location: "Kansas City, MO",
      description: "4-day intensive camp with NBA legend Larry Hughes and college coaches.",
      ages: "Ages 14-18",
      price: "$495",
      features: ["College Coach Instruction", "Video Analysis", "Recruiting Guidance", "Competition Games"]
    },
    {
      id: 2,
      title: `${sportName} Fundamentals Camp`,
      date: "JULY 8-12, 2024",
      location: "Denver, CO",
      description: "5-day camp focusing on skill development and fundamentals.",
      ages: "Ages 12-17",
      price: "$350",
      features: ["Skills Training", "Drills & Conditioning", "Scrimmages", "Awards Ceremony"]
    },
    {
      id: 3,
      title: `Elite ${sportName} Training Camp`,
      date: "AUGUST 5-9, 2024",
      location: "Orlando, FL",
      description: "Advanced training camp for elite players looking to take their game to the next level.",
      ages: "Ages 16-18",
      price: "$575",
      features: ["College Prep Training", "Mental Conditioning", "Nutrition Education", "Showcase Games"]
    }
  ];

  // Format NCAA Calendar events for display - show all sport-specific and general events
  const formatNcaaCalendarEvents = () => {
    console.log('🔍 Showing NCAA events for current sport and general events');
    
    // Show all NCAA events - the hook already filters for current sport + general events
    const filteredEvents = ncaaCalendarEvents;
    
    console.log('🔍 Showing NCAA events:', {
      totalNcaaEvents: ncaaCalendarEvents.length,
      eventsToShow: filteredEvents.length
    });
    
    return filteredEvents.map(event => ({
      id: event.id,
      date: parseLocalDate(event.start_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      event: event.title,
      description: event.description || 'Important NCAA recruiting date',
      category: event.category,
      division: event.division,
      start_date: event.start_date,
      end_date: event.end_date
    }));
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format date range for display
  const formatDateRange = (startDate: string, endDate?: string | null) => {
    if (!endDate || endDate === startDate) {
      return formatDate(startDate);
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      // Same month
      return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.getDate()}, ${start.getFullYear()}`;
    } else {
      // Different months
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
  };

  // Get category badge color
  const getCategoryColor = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'ncaa_contact':
        return 'bg-green-100 text-green-800';
      case 'ncaa_dead':
        return 'bg-red-100 text-red-800';
      case 'ncaa_evaluation':
        return 'bg-blue-100 text-blue-800';
      case 'ncaa_quiet':
        return 'bg-yellow-100 text-yellow-800';
      case 'fafsa':
        return 'bg-purple-100 text-purple-800';
      case 'testing':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get category display name
  const getCategoryName = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'ncaa_contact':
        return 'Contact Period';
      case 'ncaa_dead':
        return 'Dead Period';
      case 'ncaa_evaluation':
        return 'Evaluation Period';
      case 'ncaa_quiet':
        return 'Quiet Period';
      case 'fafsa':
        return 'FAFSA';
      case 'testing':
        return 'Testing';
      default:
        return category || 'Other';
    }
  };

  // Filter functions
  const eventTypes = ["All Types", "Showcase", "Camp", "Combine", "Expo"];

  const filteredEvents = events.filter(event => {
    return selectedEventType === "All Types" || event.type === selectedEventType;
  });

  // Handle calendar event click
  const handleCalendarEventClick = (event: any) => {
    setSelectedEvent(event);
  };

  // Filter calendar events for this sport if possible
  const sportSpecificCalendarEvents = calendarEvents.filter(event => {
    // If the event has a sport association, check if it matches
    // Otherwise include all events for broader visibility
    return true; // For now, show all events - could add sport filtering logic later
  });

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-60">
          <img 
            src={heroImage}
            alt={`${sportName} hero image`}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
              {sportName.toUpperCase()}
              <span className="block text-secondary">CENTRAL</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {sportDescription}. Connect with coaches, showcase your skills, and advance your athletic career.
            </p>
          </div>
        </div>
      </section>

      {/* Sports Hub Messages */}
      <div className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SportsHubMessages 
            sportId={currentSportId} 
            sportName={sportName} 
          />
        </div>
      </div>

      {/* Athlete profiles live on RecruitLook */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
            {sportName.toUpperCase()}
            <span className="block text-secondary">ATHLETES</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Athlete profiles live on RecruitLook.
          </p>
          <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white font-black px-8 py-4 text-lg">
            <a href="https://recruitlook.com/athletes" target="_blank" rel="noopener noreferrer">
              BROWSE ATHLETES ON RECRUITLOOK
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>

      {/* NCAA Calendar Section - Only show if calendar events exist */}
      {(ncaaCalendarLoading || ncaaCalendarEvents.length > 0) && (
        <section className="py-24 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-black mb-6 text-white">
                NCAA
                <span className="block text-secondary">CALENDAR</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Important dates and deadlines for {sportName.toLowerCase()} recruiting and eligibility.
              </p>
            </div>

            {/* NCAA Calendar Events List */}
            {ncaaCalendarLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div 
                    key={index}
                    className="bg-slate-800 rounded-2xl p-6 animate-pulse"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-5 h-5 bg-gray-600 rounded" />
                          <div className="w-32 h-4 bg-gray-600 rounded" />
                        </div>
                        <div className="w-64 h-6 bg-gray-600 rounded mb-2" />
                        <div className="w-96 h-4 bg-gray-600 rounded" />
                      </div>
                      <div className="w-20 h-8 bg-gray-600 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : ncaaCalendarEvents.length > 0 ? (
              <div className="space-y-4">
                {ncaaCalendarEvents.map((event, index) => (
                  <div 
                    key={event.id || index}
                    className="bg-slate-800 rounded-2xl p-6 hover:bg-slate-700 transition-colors duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Calendar className="w-5 h-5 text-secondary" />
                          <span className="text-secondary font-bold">
                            {formatDate(event.start_date)}
                          </span>
                          {event.division && (
                            <Badge variant="secondary" className="text-xs">
                              Division {event.division}
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">{event.title}</h3>
                        <p className="text-gray-300">
                          {event.description || 'Important NCAA recruiting date'}
                        </p>
                      </div>
                      <Badge className={getCategoryColor(event.category)}>
                        {getCategoryName(event.category)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400 mb-2">No Calendar Events</h3>
                <p className="text-gray-500">
                  No NCAA calendar events are currently available. Check back later for updates.
                </p>
              </div>
            )}
          </div>
        </section>
      )}


      {/* Events Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              {sportName.toUpperCase()}
              <span className="block text-secondary">EVENTS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase your skills at premier {sportName.toLowerCase()} events with college coaches in attendance.
            </p>
          </div>

          {/* Events Grid */}
          {eventsLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, index) => (
                <Card key={index} className="overflow-hidden border border-gray-200 animate-pulse">
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <div className="h-6 bg-gray-200 rounded mb-4 w-20"></div>
                      <div className="h-6 bg-gray-200 rounded mb-3"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-10 bg-gray-200 rounded w-24"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : realEvents.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-gray-600 mb-2">No Events</h3>
              <p className="text-gray-500">
                {`Currently no upcoming ${sportName.toLowerCase()} events available. Check back soon for new events!`}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {realEvents.map((event) => (
                <Card key={event.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 bg-white border border-gray-200">
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <Badge className="mb-4 px-3 py-1 text-xs font-black bg-gray-600 text-white">
                        {event.event_type_name?.toUpperCase() || 'EVENT'}
                      </Badge>
                      <h3 className="text-2xl font-black mb-3 leading-tight">{event.name}</h3>
                      <p className="text-sm mb-4 leading-relaxed text-gray-600">
                        {event.description || `Join us for this exciting ${sportName.toLowerCase()} event.`}
                      </p>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-secondary" />
                        <span className="font-bold text-sm">
                          {event.start_date ? parseLocalDate(event.start_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'TBD'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-secondary" />
                        <span className="font-bold text-sm">
                          {event.venue_city && event.venue_state 
                            ? `${event.venue_city}, ${event.venue_state}` 
                            : event.venue_name || 'Location TBD'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-secondary" />
                        <span className="font-bold text-sm">College coaches attending</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-black text-secondary">
                        {event.pricing_notes || 'Contact for pricing'}
                      </div>
                      <Button 
                        className="font-black bg-slate-900 text-white hover:bg-slate-800"
                        onClick={() => {
                          if (event.external_registration_url) {
                            window.open(event.external_registration_url, '_blank');
                          }
                        }}
                      >
                        REGISTER
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {realEvents.length > 0 && (
            <div className="text-center mt-12">
              <Button className="bg-secondary hover:bg-secondary/90 text-slate-900 font-black px-8 py-4 text-lg">
                VIEW ALL {sportName.toUpperCase()} EVENTS
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Camps Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              {sportName.toUpperCase()}
              <span className="block text-secondary">CAMPS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Elite training camps designed to elevate your {sportName.toLowerCase()} skills and prepare you for the next level.
            </p>
          </div>

          {campsLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, index) => (
                <Card key={index} className="overflow-hidden border border-gray-200 animate-pulse">
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <div className="h-6 bg-gray-200 rounded mb-4 w-20"></div>
                      <div className="h-6 bg-gray-200 rounded mb-3"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-10 bg-gray-200 rounded w-24"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : realCamps.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-gray-600 mb-2">No Camps</h3>
              <p className="text-gray-500">
                {`Currently no upcoming ${sportName.toLowerCase()} camps available. Check back soon for new camps!`}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {realCamps.map((camp) => (
                <Card key={camp.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-200">
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <Badge className="mb-4 bg-green-600 text-white px-3 py-1 text-xs font-black">
                        CAMP
                      </Badge>
                      <h3 className="text-xl font-black mb-3 leading-tight">{camp.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        {camp.description || `Join us for this exciting ${sportName.toLowerCase()} camp.`}
                      </p>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-secondary" />
                        <span className="font-bold text-sm">
                          {camp.start_date ? parseLocalDate(camp.start_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'TBD'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-secondary" />
                        <span className="font-bold text-sm">
                          {camp.location || 'Location TBD'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-secondary" />
                        <span className="font-bold text-sm">All skill levels welcome</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-black text-secondary">
                        {camp.cost || 'Contact for pricing'}
                      </div>
                      <Button 
                        className="bg-slate-900 text-white hover:bg-slate-800 font-black"
                        onClick={() => {
                          if (camp.website_url) {
                            window.open(camp.website_url, '_blank');
                          }
                        }}
                      >
                        REGISTER
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {realCamps.length > 0 && (
            <div className="text-center mt-12">
              <Button className="bg-secondary hover:bg-secondary/90 text-slate-900 font-black px-8 py-4 text-lg">
                VIEW ALL {sportName.toUpperCase()} CAMPS
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* RSS Feeds Section */}
      <section key={`rss-${sportName}-${sportSlug}`} className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6 text-slate-900">
              LATEST
              <span className="block text-secondary">NEWS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with the latest {sportName.toLowerCase()} news and recruiting updates from around the nation.
            </p>
          </div>

          {/* RSS Feed Info */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-black text-slate-900 mb-2">
                Latest {sportName} News
              </h2>
              <p className="text-gray-600">Stay updated with the latest news and updates</p>
            </div>
            {rssFeeds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Badge 
                  variant={selectedDivision === null ? "default" : "outline"}
                  className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
                  onClick={() => {
                    setSelectedDivision(null);
                    setFilteredArticles(rssArticles);
                  }}
                >
                  All Articles
                </Badge>
                {['D1', 'D2', 'D3'].map((division) => (
                  <Badge 
                    key={division} 
                    variant={selectedDivision === division ? "default" : "outline"}
                    className="text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
                    onClick={() => {
                      setSelectedDivision(division);
                      const filtered = rssArticles.filter(article => article.division === division);
                      setFilteredArticles(filtered);
                    }}
                  >
                    {division}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* News Articles */}
          {feedsLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <Card key={index} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-6 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="flex justify-between items-center">
                        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => (
                <Card key={`${article.title}-${index}-${sportName}-${sportSlug}`} className="hover:shadow-lg transition-shadow duration-300 border border-gray-200">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 text-xs text-gray-500 mb-3">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(article.pubDate).toLocaleDateString()}</span>
                      </div>
                      <h3 className="text-lg font-black text-slate-900 mb-3 leading-tight line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {article.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        {article.source}
                      </Badge>
                      <Button 
                        variant="ghost" 
                        className="text-secondary hover:text-secondary/80 font-bold text-sm p-0"
                        onClick={() => window.open(article.link, '_blank')}
                      >
                        Read More →
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredArticles.length === 0 && !feedsLoading && rssArticles.length > 0 && (
            <div className="text-center py-12">
              <Rss className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">No Articles Found</h3>
              <p className="text-gray-500">No articles found for the selected division.</p>
            </div>
          )}

          {rssArticles.length === 0 && !feedsLoading && (
            <div className="text-center py-12">
              <Rss className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">No News Articles Available</h3>
              <p className="text-gray-500">Configure RSS feeds in the admin dashboard to display news articles.</p>
            </div>
          )}

          <div className="text-center mt-12">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white font-black px-8 py-4 text-lg">
              VIEW ALL NEWS
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-black mb-8">
            READY TO TAKE YOUR
            <span className="block text-secondary">{sportName.toUpperCase()} CAREER</span>
            <span className="block">TO THE NEXT LEVEL?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join thousands of {sportName.toLowerCase()} athletes who have achieved their college dreams through RecruitLook's proven recruiting process.
          </p>
          
          <div className="flex justify-center">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-black text-xl px-12 py-6 font-black tracking-wide">
              GET RECRUITED
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SportTemplate;